import type {
  FormOptionSourceDescriptor,
  FormOptionDescriptor,
  PrimitiveValue,
  TranslationKey,
} from '@finance/contracts';

export interface OptionAdapterContext {
  values: Record<string, unknown>;
  query?: Record<string, unknown>;
  signal?: AbortSignal;
}

export type OptionAdapter = (
  source: FormOptionSourceDescriptor,
  ctx: OptionAdapterContext,
) => Promise<FormOptionDescriptor[]>;

const adapters = new Map<string, OptionAdapter>();

type ApiOptionFetcher = (
  signal?: AbortSignal,
  query?: Record<string, unknown>,
) => Promise<unknown[]>;

const apiFetchers = new Map<string, ApiOptionFetcher>();

export function registerOptionAdapter(adapterId: string, adapter: OptionAdapter): void {
  adapters.set(adapterId, adapter);
}

export function getOptionAdapter(adapterId: string): OptionAdapter | undefined {
  return adapters.get(adapterId);
}

export function hasOptionAdapter(adapterId: string): boolean {
  return adapters.has(adapterId);
}

export function registerApiOptionFetcher(operationId: string, fetcher: ApiOptionFetcher): void {
  if (!operationId) throw new Error('API option fetcher requires an operation id');
  if (apiFetchers.has(operationId)) throw new Error(`Duplicate API option fetcher: ${operationId}`);
  apiFetchers.set(operationId, fetcher);
}

function getPathValue(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);
}

function primitive(value: unknown, path: string): PrimitiveValue {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  throw new Error(`Option source path "${path}" did not resolve to a primitive value`);
}

registerOptionAdapter('static', async () => []);

registerOptionAdapter('mock', async () => [
  { value: 'option1', labelKey: 'opt.mock.option1' as never },
  { value: 'option2', labelKey: 'opt.mock.option2' as never },
  { value: 'option3', labelKey: 'opt.mock.option3' as never },
]);

registerOptionAdapter('api', async (source, ctx) => {
  const operationId = source.operationId as string | undefined;
  const fetcher = operationId ? apiFetchers.get(operationId) : undefined;
  if (!fetcher) {
    throw new Error(`No API fetcher registered for option source operation: ${operationId ?? 'undefined'}`);
  }

  const rows = await fetcher(ctx.signal, ctx.query);
  return rows.map((row) => {
    const labelValue = getPathValue(row, source.labelPath);
    const descriptionValue = source.descriptionPath
      ? getPathValue(row, source.descriptionPath)
      : undefined;
    const usesLabelKey = source.labelPath.toLowerCase().endsWith('labelkey');
    const usesDescriptionKey = source.descriptionPath?.toLowerCase().endsWith('key') ?? false;

    return {
      value: primitive(getPathValue(row, source.valuePath), source.valuePath),
      ...(usesLabelKey
        ? { labelKey: primitive(labelValue, source.labelPath) as TranslationKey }
        : { label: String(primitive(labelValue, source.labelPath)) }),
      ...(descriptionValue === undefined
        ? {}
        : usesDescriptionKey
          ? { descriptionKey: primitive(descriptionValue, source.descriptionPath!) as TranslationKey }
          : { metadata: { description: String(primitive(descriptionValue, source.descriptionPath!)) } }),
    } satisfies FormOptionDescriptor;
  });
});
